import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import * as L from 'leaflet';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';

import { DeliveryService, DeliveryRoute, RouteStopTemplate, DriverAccount } from 'src/app/services/delivery.service';
import { CommonPopup } from 'src/app/admin-dashboard/common-popup/common-popup';
import { GuideDialogComponent } from '../guide-dialog/guide-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { ConfigService } from 'src/app/shared/config';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

// Route templates for the driver app: a named, ordered list of kantiner one
// driver works through in a shift ("Rute Oslo Nord"), plus the driver it is
// normally assigned to.
//
// Master-detail on one page because building a route is a single sitting —
// pick the kantiner, drag them into driving order, save. Splitting it across
// two routed pages would put a navigation between two halves of one thought.
//
// NOTE on *ngFor: every list bound in the template is a plain FIELD recomputed
// on change, never a getter. Getters that build arrays or objects have frozen
// this dashboard four times (company-sites, customer-mapping, site-system,
// membership-v2) because Angular re-invokes them on every change-detection pass
// and a fresh array reference restarts the cycle.
@Component({
  selector: 'app-delivery-routes',
  templateUrl: './routes.component.html',
  styleUrls: ['./routes.component.css']
})
export class DeliveryRoutesComponent implements OnInit, OnDestroy {

  // ---- route map -----------------------------------------------------------
  // All stops of the open route on one map, numbered in driving order, joined
  // by the road route (OSRM) or a straight dashed line when OSRM is not
  // reachable. Pins come from the system guides, as on the dispatch map.
  mapOpen = true;
  mapExpanded = false;
  mapBase: 'sat' | 'map' = 'sat';
  mapNote = '';
  mapKm = 0;
  mapMinutes = 0;
  private map: L.Map | null = null;
  private mapLayer: L.LayerGroup | null = null;
  private mapBaseLayers: L.TileLayer[] = [];
  private mapSignature = '';
  private mapTimer: any = null;
  private mapRun = 0;

  // The element lives inside *ngIf="selected", so it comes and goes with the
  // open route; the setter builds and tears the map down with it.
  @ViewChild('mapEl') set mapEl(el: ElementRef<HTMLDivElement> | undefined) {
    if (el && !this.map) {
      this.map = L.map(el.nativeElement, { zoomControl: true });
      this.map.setView([59.91, 10.75], 5);
      this.mapLayer = L.layerGroup().addTo(this.map);
      this.applyMapBase();
      this.mapSignature = '';
      setTimeout(() => { this.map?.invalidateSize(); this.scheduleMap(); }, 0);
    } else if (!el && this.map) {
      this.map.remove();
      this.map = null;
      this.mapLayer = null;
      this.mapBaseLayers = [];
      this.mapSignature = '';
    }
  }


  loading = true;
  error = '';
  canEdit = false;

  routes: DeliveryRoute[] = [];
  filteredRoutes: DeliveryRoute[] = [];
  routeSearch = '';

  drivers: DriverAccount[] = [];
  activeDrivers: DriverAccount[] = [];

  // Every system belonging to the selected company, and the subset not already
  // on the open route, filtered by the picker search.
  systems: any[] = [];
  availableSystems: any[] = [];
  systemSearch = '';

  selected: DeliveryRoute | null = null;
  selectedId: string | null = null;
  dirty = false;
  saving = false;

  newRouteName = '';
  creating = false;
  duplicatingId: string | null = null;

  // Bulk stop editing. `selectedStopIds` holds systemIds — a route cannot hold
  // the same kantine twice, so the systemId is the row's identity.
  selectedStopIds = new Set<string>();
  selectedCount = 0;
  /** The seven ISO weekdays as checkboxes, Monday first. */
  bulkDays = [false, false, false, false, false, false, false];

  // Run generation, from the open route.
  runDate = moment().add(1, 'day').format('YYYY-MM-DD');
  runDriverUid = '';
  generatingRun = false;

  private subs: Subscription[] = [];

  constructor(
    private svc: DeliveryService,
    private auth: AuthService,
    private config: ConfigService,
    private notifier: NotificationService,
    private dialog: MatDialog
  ) { }

  // "How to find it": floor, directions, entrance pin, photos — per SYSTEM,
  // not per route, so every route that visits the kantine shares one guide.
  openGuide(stop: RouteStopTemplate) {
    this.dialog.open(GuideDialogComponent, {
      width: '680px',
      data: {
        systemId: stop.systemId,
        systemName: stop.systemName || stop.systemId,
        siteId: stop.siteId || null
      }
    });
  }

  ngOnInit() {
    this.subs.push(this.auth.user$.subscribe(user => {
      this.canEdit = user ? this.auth.canEdit(user) : false;
    }));

    this.subs.push(this.svc.routes$().subscribe(routes => {
      this.routes = routes;
      this.applyRouteFilter();
      // Keep the open route in step with the server without discarding
      // unsaved edits — a snapshot arriving mid-edit must not wipe the form.
      if (this.selectedId && !this.dirty) {
        const fresh = routes.find(r => r.id === this.selectedId);
        if (fresh) this.selected = this.clone(fresh);
      }
      this.loading = false;
    }, err => {
      this.error = err && err.message ? err.message : 'Could not load routes';
      this.loading = false;
    }));

    this.subs.push(this.svc.systems$().subscribe(systems => {
      this.systems = systems || [];
      this.applySystemFilter();
    }));

    this.loadDrivers();
  }

  ngOnDestroy() {
    this.map?.remove();
    this.map = null;
    clearTimeout(this.mapTimer);
    this.subs.forEach(s => s.unsubscribe());
  }

  private async loadDrivers() {
    try {
      this.drivers = await this.svc.listDrivers(this.config.getCompanyId());
      this.activeDrivers = this.drivers.filter(d => d.status);
    } catch (err) {
      // A driver list failure must not stop someone building a route — the
      // driver can be attached later.
      console.log('routes: could not load drivers', err);
      this.drivers = [];
      this.activeDrivers = [];
    }
  }

  private clone(route: DeliveryRoute): DeliveryRoute {
    return JSON.parse(JSON.stringify(route));
  }

  // ---- filters (fields, not getters) ---------------------------------------

  applyRouteFilter() {
    const q = this.routeSearch.trim().toLowerCase();
    this.filteredRoutes = !q
      ? this.routes.slice()
      : this.routes.filter(r => (r.name || '').toLowerCase().includes(q));
  }

  toggleMapOpen() {
    this.mapOpen = !this.mapOpen;
    if (this.mapOpen) {
      setTimeout(() => { this.map?.invalidateSize(); this.mapSignature = ''; this.scheduleMap(); });
    }
  }

  toggleMapExpanded() {
    this.mapExpanded = !this.mapExpanded;
    // Leaflet must be told the box changed size, then re-fit the stops.
    setTimeout(() => {
      this.map?.invalidateSize();
      const pts = (this.mapLayer?.getLayers() || [])
        .filter((l: any) => l.getLatLng).map((l: any) => l.getLatLng());
      if (this.map && pts.length) this.map.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 16 });
    }, 50);
  }

  toggleMapBase() {
    this.mapBase = this.mapBase === 'sat' ? 'map' : 'sat';
    try { localStorage.setItem('mapBase', this.mapBase); } catch { }
    this.applyMapBase();
  }

  private applyMapBase() {
    if (!this.map) return;
    try { if (localStorage.getItem('mapBase') === 'map') this.mapBase = 'map'; } catch { }
    this.mapBaseLayers.forEach(l => l.remove());
    this.mapBaseLayers = this.mapBase === 'sat'
      ? [
        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          maxZoom: 21, maxNativeZoom: 19, attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics'
        }),
        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
          maxZoom: 21, maxNativeZoom: 19, attribution: ''
        })
      ]
      : [L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; OpenStreetMap'
      })];
    this.mapBaseLayers.forEach(l => l.addTo(this.map!));
  }

  // Called whenever the open route or its stops change; redraws only when the
  // stop order actually differs from what is on the map.
  private scheduleMap() {
    clearTimeout(this.mapTimer);
    this.mapTimer = setTimeout(() => this.drawMap(), 250);
  }

  private async drawMap() {
    if (!this.map || !this.mapLayer || !this.selected) return;
    const stops = this.selected.stops || [];
    const depot = this.selected.depot || null;
    const sig = this.selected.id + ':' + (depot ? depot.lat + ',' + depot.lng : '-') + ':' +
      stops.map(s => s.systemId).join(',');
    if (sig === this.mapSignature) return;
    this.mapSignature = sig;
    const run = ++this.mapRun;
    const layer = this.mapLayer;
    layer.clearLayers();
    this.mapNote = '';
    this.mapKm = 0;
    this.mapMinutes = 0;
    if (!stops.length) return;

    const points = await this.svc.getGuidePoints(stops.map(s => s.systemId));
    if (run !== this.mapRun || !this.map) return;

    const placed: { lat: number, lng: number }[] = [];
    const missing: string[] = [];
    stops.forEach((s, i) => {
      const p = points[s.systemId];
      if (!p) { missing.push(s.systemName || s.systemId); return; }
      placed.push(p);
      const pin = L.divIcon({
        className: '', iconSize: [28, 28], iconAnchor: [14, 28],
        html: `<div class="dm-pin dm-pending">${i + 1}</div>`
      });
      L.marker([p.lat, p.lng], { icon: pin, title: s.systemName || s.systemId })
        .bindPopup(`<b>${i + 1}. ${this.esc(s.systemName || s.systemId)}</b>` +
          (p.source === 'geo' ? '<br><i>approximate — geocoded address</i>' : ''))
        .addTo(layer);
    });
    this.mapNote = missing.length
      ? `${missing.length} stop${missing.length === 1 ? '' : 's'} not on the map (no location yet): ` +
        `${missing.slice(0, 4).join(', ')}${missing.length > 4 ? '…' : ''}. Set one with the place icon.`
      : '';
    if (!placed.length) return;

    // The round starts and ends at the loading point, when the route has one.
    const loop = depot ? [depot, ...placed, depot] : placed;
    if (depot) {
      L.marker([depot.lat, depot.lng], {
        title: depot.name,
        icon: L.divIcon({
          className: '', iconSize: [30, 30], iconAnchor: [15, 30],
          html: '<div class="dm-pin dm-depot">&#9873;</div>'
        })
      }).bindPopup(`<b>${this.esc(depot.name)}</b><br>Loading point — start and end of the round`).addTo(layer);
    }

    // Straight line first so something is there at once, then the road route.
    const bounds = loop.map(p => [p.lat, p.lng] as [number, number]);
    const line = L.polyline(bounds, { color: '#1d6ef2', weight: 3, opacity: .6, dashArray: '6 8' }).addTo(layer);
    this.map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 16 });

    if (loop.length < 2) return;
    const road = await this.roadRoute(loop);
    if (run !== this.mapRun || !this.map || !road) return;
    line.remove();

    // One coloured leg per stop-to-stop hop, shifting from blue (first leg) to
    // pink (last), with arrows along it. Where a route drives the same road
    // out and back, the colours and the arrow directions tell the legs apart;
    // hovering or clicking a leg names it.
    const stopNames = stops.filter(s => !!points[s.systemId]).map(s => s.systemName || s.systemId);
    const names = depot ? [depot.name, ...stopNames, depot.name] : stopNames;
    const n = road.legs.length;
    road.legs.forEach((leg, i) => {
      const hue = n > 1 ? Math.round(210 + (150 * i) / (n - 1)) : 210;
      const color = `hsl(${hue}, 80%, 50%)`;
      const label = `<b>Leg ${i + 1}</b>: ${this.esc(names[i] || '')} → ${this.esc(names[i + 1] || '')}` +
        `<br>${leg.km.toFixed(1)} km · ${Math.max(1, Math.round(leg.minutes))} min`;
      const pl = L.polyline(leg.path, { color, weight: 5, opacity: .85 })
        .bindTooltip(label, { sticky: true })
        .addTo(layer);
      pl.on('mouseover', () => pl.setStyle({ weight: 8, opacity: 1 }).bringToFront());
      pl.on('mouseout', () => pl.setStyle({ weight: 5, opacity: .85 }));
      this.arrowsAlong(leg.path, color).forEach(m => m.addTo(layer));
    });
    this.mapKm = road.km;
    this.mapMinutes = road.minutes;
  }

  // Direction arrows at a few points along a leg (about every 2 km, at least
  // one, at most five), pointing the way the driver goes.
  private arrowsAlong(path: [number, number][], color: string): L.Marker[] {
    if (path.length < 2) return [];
    const count = Math.max(1, Math.min(5, Math.round(this.pathKm(path) / 2)));
    const out: L.Marker[] = [];
    for (let k = 1; k <= count; k++) {
      const idx = Math.min(path.length - 2, Math.floor((path.length - 1) * k / (count + 1)));
      const a = path[idx], b = path[idx + 1];
      const dx = (b[1] - a[1]) * Math.cos(a[0] * Math.PI / 180), dy = b[0] - a[0];
      const bearing = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;
      out.push(L.marker(a, {
        interactive: false, keyboard: false,
        icon: L.divIcon({
          className: '', iconSize: [16, 16], iconAnchor: [8, 8],
          html: `<div class="dr-arrow" style="transform:rotate(${bearing}deg);border-bottom-color:${color}"></div>`
        })
      }));
    }
    return out;
  }

  private pathKm(path: [number, number][]): number {
    let d = 0;
    for (let i = 1; i < path.length; i++) {
      d += this.km({ lat: path[i - 1][0], lng: path[i - 1][1] }, { lat: path[i][0], lng: path[i][1] });
    }
    return d;
  }

  private async roadRoute(pts: { lat: number, lng: number }[]):
    Promise<{ legs: { path: [number, number][], km: number, minutes: number }[], km: number, minutes: number } | null> {
    try {
      const coords = pts.map(p => `${p.lng},${p.lat}`).join(';');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${coords}?overview=false&steps=true&geometries=geojson`,
        { signal: controller.signal });
      clearTimeout(timer);
      if (!res.ok) return null;
      const data = await res.json();
      const r = data && data.routes && data.routes[0];
      if (data.code !== 'Ok' || !r) return null;
      const legs = (r.legs || []).map((leg: any) => ({
        path: [].concat(...(leg.steps || []).map((st: any) =>
          st.geometry.coordinates.map((c: number[]) => [c[1], c[0]] as [number, number]))),
        km: leg.distance / 1000,
        minutes: leg.duration / 60
      }));
      if (!legs.length) return null;
      return { legs, km: r.distance / 1000, minutes: r.duration / 60 };
    } catch {
      return null;
    }
  }

  private esc(s: string) {
    const map: any = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s).replace(/[&<>"']/g, c => map[c]);
  }

  applySystemFilter() {
    this.scheduleMap();
    const taken = new Set((this.selected?.stops || []).map(s => s.systemId));
    const q = this.systemSearch.trim().toLowerCase();
    this.availableSystems = this.systems.filter(s => {
      if (taken.has(s.id)) return false;
      if (!q) return true;
      return (s.name || '').toLowerCase().includes(q)
        || String(s.number || '').toLowerCase().includes(q);
    });
  }

  // ---- selection -----------------------------------------------------------

  select(route: DeliveryRoute) {
    if (this.dirty && !confirm('Discard unsaved changes to this route?')) return;
    this.selected = this.clone(route);
    this.selectedId = route.id || null;
    this.runDriverUid = route.defaultDriverUid || '';
    this.dirty = false;
    this.systemSearch = '';
    this.clearStopSelection();
    this.optimProposal = null;
    this.optimMessage = '';
    this.applySystemFilter();
  }

  closeEditor() {
    if (this.dirty && !confirm('Discard unsaved changes to this route?')) return;
    this.selected = null;
    this.selectedId = null;
    this.dirty = false;
  }

  // ---- create / delete -----------------------------------------------------

  // Name, default driver and active flag — saved straight away; the stops are
  // saved with "Save route" as before.
  openEdit() {
    const route = this.selected;
    if (!route || !route.id) return;
    const id = route.id;
    this.dialog.open(CommonPopup, {
      disableClose: true,
      width: '460px',
      data: {
        formData: {
          title: 'Edit route',
          inputFeilds: [
            { labelName: 'Route name', name: 'name', type: 'text', required: true, value: route.name },
            {
              labelName: 'Default driver', name: 'driver', required: false,
              value: route.defaultDriverUid || 'none',
              selectOptions: [
                { value: 'none', label: '— none —' },
                ...this.activeDrivers.map(d => ({
                  value: d.uid, label: (d.name || d.email) + (d.employer ? ' · ' + d.employer : '')
                }))
              ]
            },
            { labelName: 'Active', name: 'active', type: 'checkbox', value: route.active !== false },
            { labelName: 'Loading point name', name: 'depotName', type: 'text', required: false,
              value: route.depot?.name || '', hint: 'Where the vans are loaded, e.g. "Lager Alnabru".' },
            { labelName: 'Loading point location', name: 'depotWhere', type: 'text', required: false,
              value: route.depot ? `${route.depot.lat}, ${route.depot.lng}` : '',
              hint: 'Coordinates like 59.9311, 10.8123 (copy them from Google Maps) or an address. Leave empty for none.' },
            { labelName: 'Use this loading point on all routes', name: 'depotAll', type: 'checkbox', value: false }
          ],
          buttons: [{ name: 'SAVE', type: 'submit', color: 'primary', validate: true }],
          onSubmit: async (v: any) => {
            const name = (v.name || '').trim();
            if (!name) return { success: false, message: 'Enter a name for the route' };
            const driverUid = v.driver && v.driver !== 'none' ? v.driver : null;
            const driver = this.drivers.find(d => d.uid === driverUid);
            const where = (v.depotWhere || '').trim();
            let depot: { name: string, lat: number, lng: number } | null = null;
            if (where) {
              let p = this.parseCoordinates(where);
              if (!p) {
                try { p = await this.geocode(where); } catch { p = null; }
              }
              if (!p) {
                return { success: false, message: 'Could not find that location. Use coordinates like "59.9311, 10.8123".' };
              }
              depot = { name: (v.depotName || '').trim() || 'Loading point', lat: p.lat, lng: p.lng };
            }
            const patch = {
              name,
              active: v.active === true,
              defaultDriverUid: driverUid,
              defaultDriverName: driver ? (driver.name || driver.email) : '',
              depot
            };
            try {
              await this.svc.saveRoute(id, patch);
              if (v.depotAll === true) {
                for (const other of this.routes) {
                  if (other.id && other.id !== id) await this.svc.saveRoute(other.id, { depot });
                }
              }
            } catch (err: any) {
              return { success: false, message: err?.message || 'Could not save the route' };
            }
            this.mapSignature = '';
            this.scheduleMap();
            if (this.selected && this.selected.id === id) {
              Object.assign(this.selected, patch);
              this.runDriverUid = patch.defaultDriverUid || '';
            }
            return { success: true };
          }
        }
      }
    });
  }

  // "59.9311, 10.8123" (Google Maps order: latitude first), also with a
  // semicolon or just spaces between. Anything else is treated as an address.
  private parseCoordinates(text: string): { lat: number, lng: number } | null {
    const m = text.trim().match(/^(-?\d+(?:[.,]\d+)?)\s*[,; ]\s*(-?\d+(?:[.,]\d+)?)$/);
    if (!m) return null;
    const lat = parseFloat(m[1].replace(',', '.')), lng = parseFloat(m[2].replace(',', '.'));
    return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
  }

  // The + button: ask for the name, then create and open the route.
  openNewRoute() {
    const ref = this.dialog.open(CommonPopup, {
      disableClose: true,
      width: '460px',
      data: {
        formData: {
          title: 'New route',
          inputFeilds: [
            { labelName: 'Route name', name: 'name', type: 'text', required: true, value: '',
              hint: 'For example "Rute Oslo Nord".' }
          ],
          buttons: [{ name: 'CREATE ROUTE', type: 'submit', color: 'primary', validate: true }],
          onSubmit: async (v: any) => {
            const name = (v.name || '').trim();
            if (!name) return { success: false, message: 'Enter a name for the route' };
            this.newRouteName = name;
            await this.createRoute();
            return { success: true };
          }
        }
      }
    });
  }

  async createRoute() {
    const name = this.newRouteName.trim();
    if (!name) return;
    this.creating = true;
    try {
      const ref: any = await this.svc.createRoute(name);
      this.newRouteName = '';
      this.notifier.showNotification('success', 'Route created');
      // The snapshot will arrive on its own; open it straight away so the
      // next thing the operator does is add kantiner.
      // Almost every route loads at the same place, so a new one starts with
      // the loading point of an existing route (changeable in Edit).
      const inherited = (this.routes.find(r => !!r.depot) || {} as any).depot || null;
      if (inherited) await this.svc.saveRoute(ref.id, { depot: inherited });
      this.selected = {
        id: ref.id, name, companyId: this.config.getCompanyId(),
        active: true, stops: [], defaultDriverUid: null, defaultDriverName: '',
        depot: inherited
      };
      this.selectedId = ref.id;
      this.dirty = false;
      this.mapSignature = '';
      this.applySystemFilter();
    } catch (err: any) {
      this.notifier.showNotification('error', err?.message || 'Could not create the route');
    }
    this.creating = false;
  }

  async deleteRoute(route: DeliveryRoute) {
    if (!confirm(`Delete the route "${route.name}"? Runs already generated from it are not affected.`)) return;
    try {
      await this.svc.deleteRoute(route.id!);
      if (this.selectedId === route.id) {
        this.selected = null;
        this.selectedId = null;
        this.dirty = false;
      }
      this.notifier.showNotification('success', 'Route deleted');
    } catch (err: any) {
      this.notifier.showNotification('error', err?.message || 'Could not delete the route');
    }
  }

  // ---- stop editing --------------------------------------------------------

  addStop(system: any) {
    if (!this.selected) return;
    this.selected.stops = this.selected.stops || [];
    if (this.selected.stops.some(s => s.systemId === system.id)) return;
    this.selected.stops.push({
      systemId: system.id,
      siteId: system.siteId || null,
      systemName: system.name || '',
      siteName: system.siteName || '',
      order: this.selected.stops.length + 1,
      note: '',
      expectedMinutes: null
    });
    this.renumber();
    this.markDirty();
    this.applySystemFilter();
  }

  removeStop(index: number) {
    if (!this.selected) return;
    const gone = this.selected.stops[index];
    if (gone) {
      this.selectedStopIds.delete(gone.systemId);
      this.selectedCount = this.selectedStopIds.size;
    }
    this.selected.stops.splice(index, 1);
    this.renumber();
    this.markDirty();
    this.applySystemFilter();
  }

  dropStop(event: CdkDragDrop<RouteStopTemplate[]>) {
    if (!this.selected) return;
    moveItemInArray(this.selected.stops, event.previousIndex, event.currentIndex);
    this.renumber();
    this.markDirty();
  }

  // `order` is stored, not derived, because the driver app reads stops with a
  // plain where(runId) query and sorts on this field.
  private renumber() {
    (this.selected?.stops || []).forEach((s, i) => s.order = i + 1);
  }

  markDirty() {
    this.dirty = true;
  }

  onDriverChange() {
    if (!this.selected) return;
    const d = this.drivers.find(x => x.uid === this.selected!.defaultDriverUid);
    this.selected.defaultDriverName = d ? (d.name || d.email) : '';
    // The run generator below defaults to the route's driver; keep it in step
    // rather than making the operator pick the same person twice.
    this.runDriverUid = this.selected.defaultDriverUid || '';
    this.markDirty();
  }

  async save() {
    if (!this.selected || !this.selectedId) return;
    const name = (this.selected.name || '').trim();
    if (!name) {
      this.notifier.showNotification('warning', 'The route needs a name');
      return;
    }
    this.saving = true;
    try {
      await this.svc.saveRoute(this.selectedId, {
        name,
        active: this.selected.active !== false,
        stops: this.selected.stops || [],
        defaultDriverUid: this.selected.defaultDriverUid || null,
        defaultDriverName: this.selected.defaultDriverName || ''
      });
      this.dirty = false;
      this.notifier.showNotification('success', 'Route saved');
    } catch (err: any) {
      this.notifier.showNotification('error', err?.message || 'Could not save the route');
    }
    this.saving = false;
  }

  // ---- weekdays ------------------------------------------------------------
  // `weekdays` is ISO days (1=Mon … 7=Sun) on the stop template. ABSENT means
  // Mon–Fri: the cloud function's stopDueOn() treats an empty field that way so
  // an imported round does not quietly schedule Saturdays. It drives which
  // stops are materialised on the daily auto-generation; a manual "Generate
  // run" deliberately takes the whole route.
  //
  // Until now nothing in the admin could edit this field at all — it existed
  // only for rows the workbook importer guessed at.

  private static readonly DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  dayNames = DeliveryRoutesComponent.DAY_NAMES;

  weekdaysLabel(stop: RouteStopTemplate): string {
    const d = stop.weekdays;
    if (!Array.isArray(d) || !d.length) return 'Mon–Fri';
    const sorted = d.slice().sort((a, b) => a - b);
    if (sorted.join() === '1,2,3,4,5') return 'Mon–Fri';
    if (sorted.join() === '1,2,3,4,5,6,7') return 'Every day';
    return sorted.map(n => DeliveryRoutesComponent.DAY_NAMES[n - 1] || n).join(', ');
  }

  /** True when the stop is not on the default Mon–Fri, i.e. worth noticing. */
  isCustomWeekdays(stop: RouteStopTemplate): boolean {
    const d = stop.weekdays;
    return Array.isArray(d) && d.length > 0 && d.slice().sort((a, b) => a - b).join() !== '1,2,3,4,5';
  }

  isStopSelected(stop: RouteStopTemplate): boolean {
    return this.selectedStopIds.has(stop.systemId);
  }

  toggleStopSelection(stop: RouteStopTemplate) {
    if (this.selectedStopIds.has(stop.systemId)) this.selectedStopIds.delete(stop.systemId);
    else this.selectedStopIds.add(stop.systemId);
    this.selectedCount = this.selectedStopIds.size;
  }

  selectAllStops() {
    (this.selected?.stops || []).forEach(s => this.selectedStopIds.add(s.systemId));
    this.selectedCount = this.selectedStopIds.size;
  }

  clearStopSelection() {
    this.selectedStopIds.clear();
    this.selectedCount = 0;
  }

  /** Load the days of the first selected stop into the day checkboxes. */
  loadDaysFromSelection() {
    const first = (this.selected?.stops || []).find(s => this.selectedStopIds.has(s.systemId));
    const days = Array.isArray(first?.weekdays) && first!.weekdays!.length ? first!.weekdays! : [1, 2, 3, 4, 5];
    this.bulkDays = [1, 2, 3, 4, 5, 6, 7].map(n => days.includes(n));
  }

  applyWeekdays() {
    if (!this.selected || !this.selectedCount) return;
    const days = this.bulkDays
      .map((on, i) => on ? i + 1 : 0)
      .filter(n => n > 0);
    if (!days.length) {
      this.notifier.showNotification('warning',
        'Pick at least one day, or use "Back to Mon–Fri" to clear the setting');
      return;
    }
    // Mon–Fri is the default, so store it as absent rather than as a list —
    // one representation of "the normal week", not two.
    const value = days.join() === '1,2,3,4,5' ? null : days;
    (this.selected.stops || []).forEach(s => {
      if (this.selectedStopIds.has(s.systemId)) s.weekdays = value;
    });
    this.markDirty();
    this.notifier.showNotification('success',
      `Days set on ${this.selectedCount} stop${this.selectedCount === 1 ? '' : 's'} — remember to save`);
  }

  resetWeekdays() {
    if (!this.selected || !this.selectedCount) return;
    (this.selected.stops || []).forEach(s => {
      if (this.selectedStopIds.has(s.systemId)) s.weekdays = null;
    });
    this.markDirty();
  }

  removeSelectedStops() {
    if (!this.selected || !this.selectedCount) return;
    if (!confirm(`Remove ${this.selectedCount} kantiner from this route?`)) return;
    this.selected.stops = (this.selected.stops || []).filter(s => !this.selectedStopIds.has(s.systemId));
    this.clearStopSelection();
    this.renumber();
    this.markDirty();
    this.applySystemFilter();
  }

  // ---- duplicate -----------------------------------------------------------
  // The client builds similar rounds over and over (a summer variant, a second
  // van on the same area). Copying keeps every stop field — weekdays, services,
  // notes, minutes — because a copy that silently drops them is worse than no
  // copy at all.

  // Asks for the name of the copy first, then creates it.
  duplicateRoute(route: DeliveryRoute, event?: Event) {
    event?.stopPropagation();
    if (!route.id || this.duplicatingId) return;
    let newId: string | null = null;
    let newName = '';
    const ref = this.dialog.open(CommonPopup, {
      disableClose: true,
      width: '460px',
      data: {
        formData: {
          title: `Copy route "${route.name}"`,
          inputFeilds: [
            { labelName: 'Name of the new route', name: 'name', type: 'text', required: true,
              value: `${route.name || 'Route'} (copy)` }
          ],
          buttons: [{ name: 'COPY ROUTE', type: 'submit', color: 'primary', validate: true }],
          onSubmit: async (v: any) => {
            const name = (v.name || '').trim();
            if (!name) return { success: false, message: 'Enter a name for the new route' };
            this.duplicatingId = route.id!;
            try {
              const created: any = await this.svc.createRoute(name);
              await this.svc.saveRoute(created.id, {
                stops: JSON.parse(JSON.stringify(route.stops || [])),
                defaultDriverUid: route.defaultDriverUid || null,
                defaultDriverName: route.defaultDriverName || '',
                depot: route.depot || null,
                active: route.active !== false
              });
              newId = created.id;
              newName = name;
              return { success: true };
            } catch (err: any) {
              return { success: false, message: err?.message || 'Could not copy the route' };
            } finally {
              this.duplicatingId = null;
            }
          }
        }
      }
    });
    ref.afterClosed().subscribe(res => {
      if (!res || !newId) return;
      this.notifier.showNotification('success', `Copied to "${newName}"`);
      if (!this.dirty) {
        this.selected = {
          id: newId, name: newName, companyId: this.config.getCompanyId(),
          active: route.active !== false,
          stops: JSON.parse(JSON.stringify(route.stops || [])),
          defaultDriverUid: route.defaultDriverUid || null,
          defaultDriverName: route.defaultDriverName || '',
          depot: route.depot || null
        };
        this.selectedId = newId;
        this.runDriverUid = route.defaultDriverUid || '';
        this.clearStopSelection();
        this.applySystemFilter();
      }
    });
  }

  // ---- driver assignment from the list --------------------------------------
  // Handing a finished round to a driver used to mean: open the route, change
  // the select, press Save. From the list it is two clicks and it is stored.

  async assignDriver(route: DeliveryRoute, driver: DriverAccount | null, event?: Event) {
    event?.stopPropagation();
    if (!route.id) return;
    if (this.dirty && this.selectedId === route.id) {
      this.notifier.showNotification('warning', 'Save or discard the open changes first');
      return;
    }
    try {
      await this.svc.saveRoute(route.id, {
        defaultDriverUid: driver ? driver.uid : null,
        defaultDriverName: driver ? (driver.name || driver.email) : ''
      });
      if (this.selected && this.selectedId === route.id) {
        this.selected.defaultDriverUid = driver ? driver.uid : null;
        this.selected.defaultDriverName = driver ? (driver.name || driver.email) : '';
        this.runDriverUid = driver ? driver.uid : '';
      }
      this.notifier.showNotification('success',
        driver ? `${route.name} → ${driver.name || driver.email}` : `${route.name} has no driver now`);
    } catch (err: any) {
      this.notifier.showNotification('error', err?.message || 'Could not assign the driver');
    }
  }

  trackByDriver(_: number, d: DriverAccount) { return d.uid; }

  // ---- run generation ------------------------------------------------------

  async generateRun() {
    if (!this.selectedId) return;
    if (this.dirty) {
      this.notifier.showNotification('warning', 'Save the route before generating a run');
      return;
    }
    if (!this.selected?.stops?.length) {
      this.notifier.showNotification('warning', 'Add at least one kantine first');
      return;
    }
    this.generatingRun = true;
    try {
      const res: any = await this.svc.generateRun(
        this.selectedId, this.runDate, this.runDriverUid || null);
      this.notifier.showNotification('success',
        `Run created for ${this.runDate} — ${res.stopCount} stops`);
    } catch (err: any) {
      // The server refuses to regenerate a run a driver has already started;
      // that message is the useful one, so surface it rather than a generic.
      const msg = err?.error?.message || err?.message || 'Could not generate the run';
      this.notifier.showNotification('error', msg);
    }
    this.generatingRun = false;
  }

  // ---- route optimization --------------------------------------------------
  // Straight-line nearest-neighbour + 2-opt over the stops' coordinates,
  // anchored on the current first stop (the depot's position is not recorded,
  // and the operator chose stop 1 for a reason). It PROPOSES; the operator
  // applies or dismisses — the button never rewrites a route on its own.
  //
  // Coordinates come from systemGuides: the curated entrance pin when it
  // exists, else a cached geocode (`geo`), else a fresh Nominatim lookup of the
  // stop's address, saved back so it is never fetched twice.

  optimizing = false;
  optimStep = '';
  optimMessage = '';
  optimProposal: { stops: RouteStopTemplate[], beforeKm: number, afterKm: number } | null = null;

  async optimizeOrder() {
    if (!this.selected || this.optimizing) return;
    const stops = this.selected.stops || [];
    if (stops.length < 3) {
      this.optimMessage = 'Nothing to optimize with fewer than three stops.';
      return;
    }
    this.optimizing = true;
    this.optimMessage = '';
    this.optimProposal = null;

    try {
      // 1. Collect a coordinate per stop, geocoding and caching the gaps.
      const coords = new Map<string, { lat: number, lng: number }>();
      const missing: string[] = [];
      for (const s of stops) {
        this.optimStep = `Locating ${s.systemName || s.systemId}…`;
        const g = await this.svc.getGuide(s.systemId);
        let p = this.point(g?.entrance) || this.point(g?.geo);
        if (!p && s.address) {
          p = await this.geocode(s.address);
          if (p) await this.svc.saveGuide(s.systemId, { geo: { ...p, source: 'nominatim' } });
        }
        if (p) coords.set(s.systemId, p);
        else missing.push(s.systemName || s.systemId);
      }

      if (missing.length) {
        this.optimMessage = `No location found for: ${missing.join(', ')}. ` +
          `Set an entrance pin (place icon) or an address, then try again.`;
        return;
      }

      // 2. Distance matrix: real road distances from OSRM when it answers
      // (fjords and one-way industrial areas beat straight lines), haversine
      // when it does not — the optimize button must work either way.
      this.optimStep = 'Measuring distances…';
      const pts = stops.map(s => coords.get(s.systemId)!);
      const matrix = await this.roadMatrix(pts) ?? this.haversineMatrix(pts);

      this.optimStep = 'Computing order…';
      const beforeKm = this.tourKm(pts.map((_, i) => i), matrix);
      const order = this.twoOpt(this.nearestNeighbour(matrix), matrix);
      const afterKm = this.tourKm(order, matrix);

      if (beforeKm - afterKm < beforeKm * 0.02) {
        this.optimMessage = `The current order is already good (${beforeKm.toFixed(1)} km between stops).`;
        return;
      }
      this.optimProposal = {
        stops: order.map(i => stops[i]),
        beforeKm, afterKm
      };
    } catch (err: any) {
      this.optimMessage = err?.message || 'Optimization failed — try again.';
    } finally {
      this.optimizing = false;
      this.optimStep = '';
    }
  }

  applyOptimization() {
    if (!this.selected || !this.optimProposal) return;
    this.selected.stops = this.optimProposal.stops;
    this.renumber();
    this.markDirty();
    this.optimProposal = null;
    this.optimMessage = 'New order applied — remember to save the route.';
  }

  dismissOptimization() {
    this.optimProposal = null;
    this.optimMessage = '';
  }

  private point(p: any): { lat: number, lng: number } | null {
    return p && typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng } : null;
  }

  // Nominatim, one lookup per second per their usage policy. Results are
  // cached into systemGuides.geo above, so a route geocodes exactly once.
  private async geocode(address: string): Promise<{ lat: number, lng: number } | null> {
    await new Promise(r => setTimeout(r, 1100));
    const q = encodeURIComponent(address);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=no&q=${q}`);
    if (!res.ok) return null;
    const arr = await res.json();
    if (!Array.isArray(arr) || !arr.length) return null;
    const lat = parseFloat(arr[0].lat), lng = parseFloat(arr[0].lon);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  private km(a: { lat: number, lng: number }, b: { lat: number, lng: number }): number {
    const R = 6371, rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    const h = Math.sin(dLat / 2) ** 2
      + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  private haversineMatrix(pts: { lat: number, lng: number }[]): number[][] {
    return pts.map(a => pts.map(b => this.km(a, b)));
  }

  // Road distances from the public OSRM demo server, km. Null on any trouble
  // (rate limit, downtime, weird payload) — the caller falls back to
  // straight lines rather than failing the button.
  private async roadMatrix(pts: { lat: number, lng: number }[]): Promise<number[][] | null> {
    try {
      const coords = pts.map(p => `${p.lng},${p.lat}`).join(';');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(
        `https://router.project-osrm.org/table/v1/driving/${coords}?annotations=distance`,
        { signal: controller.signal });
      clearTimeout(timer);
      if (!res.ok) return null;
      const data = await res.json();
      if (data.code !== 'Ok' || !Array.isArray(data.distances)) return null;
      // Meters → km; a null cell (unroutable pair) falls back per-cell.
      return data.distances.map((row: any[], i: number) => row.map((m: any, j: number) =>
        typeof m === 'number' ? m / 1000 : this.km(pts[i], pts[j])));
    } catch {
      return null;
    }
  }

  private tourKm(order: number[], matrix: number[][]): number {
    let d = 0;
    for (let i = 1; i < order.length; i++) d += matrix[order[i - 1]][order[i]];
    return d;
  }

  private nearestNeighbour(matrix: number[][]): number[] {
    const order = [0];
    const left = new Set(matrix.map((_, i) => i).slice(1));
    while (left.size) {
      const cur = order[order.length - 1];
      let best = -1, bestD = Infinity;
      for (const i of left) {
        if (matrix[cur][i] < bestD) { bestD = matrix[cur][i]; best = i; }
      }
      order.push(best);
      left.delete(best);
    }
    return order;
  }

  private twoOpt(order: number[], matrix: number[][]): number[] {
    let improved = true;
    const o = order.slice();
    while (improved) {
      improved = false;
      for (let i = 1; i < o.length - 1; i++) {
        for (let j = i + 1; j < o.length; j++) {
          const before = matrix[o[i - 1]][o[i]]
            + (j + 1 < o.length ? matrix[o[j]][o[j + 1]] : 0);
          const after = matrix[o[i - 1]][o[j]]
            + (j + 1 < o.length ? matrix[o[i]][o[j + 1]] : 0);
          if (after < before - 1e-9) {
            o.splice(i, j - i + 1, ...o.slice(i, j + 1).reverse());
            improved = true;
          }
        }
      }
    }
    return o;
  }

  driverLabel(uid: string | null | undefined): string {
    if (!uid) return '—';
    const d = this.drivers.find(x => x.uid === uid);
    return d ? (d.name || d.email) : 'Unknown driver';
  }

  trackByStop(index: number, stop: RouteStopTemplate) {
    return stop.systemId;
  }

  trackByRoute(index: number, route: DeliveryRoute) {
    return route.id;
  }

  trackBySystem(index: number, system: any) {
    return system.id;
  }
}
